/**
 * Game Setup Config Component
 * View and edit game settings for an outing.
 */
import React, { useState } from 'react';
import { Settings, Save } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import { Switch } from '../../../components/ui/switch';
import { Textarea } from '../../../components/ui/textarea';
import { toast } from 'sonner';
import { useOutingApi } from '../hooks/useOutingApi';
import { GAME_FORMATS } from '../utils/outingHelpers';

export default function GameSetupConfig({ outing, user, onUpdate }) {
  const api = useOutingApi();
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState(outing?.game_settings || {});

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.updateOuting(outing.id, { game_settings: settings }, user.user_id);
      toast.success('Settings updated!');
      setEditing(false);
      onUpdate?.();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const format = GAME_FORMATS[settings.format];
  const isLocked = ['in_progress', 'review', 'completed'].includes(outing?.status);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-bold text-white">Game Settings</h3>
        {!isLocked && !editing && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => setEditing(true)}
            className="border-white/20"
          >
            <Settings className="w-4 h-4 mr-2" />
            Edit
          </Button>
        )}
        {editing && (
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                setSettings(outing?.game_settings || {});
                setEditing(false);
              }}
              className="border-white/20"
            >
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={handleSave}
              disabled={saving}
              className="bg-[#007AFF] hover:bg-[#38BDF8]"
            >
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </div>
        )}
      </div>

      {isLocked && (
        <Card className="bg-yellow-500/10 border-yellow-500/30">
          <CardContent className="p-3 text-center text-yellow-300 text-sm">
            Settings are locked once the event is in progress
          </CardContent>
        </Card>
      )}

      {/* Format */}
      <Card className="bg-[#121212] border-white/10">
        <CardHeader>
          <CardTitle className="text-white text-base">Game Format</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-3">
            <span className="text-3xl">{format?.icon || '🏌️'}</span>
            <div>
              <p className="font-semibold text-white">{format?.name || settings.format}</p>
              <p className="text-sm text-zinc-400">{format?.description}</p>
            </div>
          </div>
          <div className="mt-4 flex gap-2">
            <Badge variant="outline">{settings.team_size || 4}-Person Teams</Badge>
            {settings.use_handicaps && (
              <Badge variant="outline" className="border-green-500 text-green-400">
                Handicaps Enabled
              </Badge>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Skins */}
      <Card className="bg-[#121212] border-white/10">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-white text-base">Skins Game</CardTitle>
            {editing ? (
              <Switch
                checked={settings.skins?.enabled}
                onCheckedChange={(v) => setSettings({
                  ...settings,
                  skins: { ...settings.skins, enabled: v }
                })}
              />
            ) : (
              <Badge className={settings.skins?.enabled ? 'bg-green-500/20 text-green-400' : 'bg-zinc-500/20 text-zinc-400'}>
                {settings.skins?.enabled ? 'Enabled' : 'Disabled'}
              </Badge>
            )}
          </div>
        </CardHeader>
        {settings.skins?.enabled && (
          <CardContent className="pt-2">
            <div className="flex gap-4 text-sm">
              <span className="text-zinc-400">Total pot:</span>
              <span className="text-white font-semibold">${settings.skins?.total_pot || 0}</span>
              <span className="text-zinc-500">(${((settings.skins?.total_pot || 0) / 18).toFixed(2)}/hole)</span>
            </div>
            <div className="flex gap-4 text-sm mt-1">
              <span className="text-zinc-400">Carryover:</span>
              <span className="text-white">{settings.skins?.carryover ? 'Yes' : 'No'}</span>
            </div>
          </CardContent>
        )}
      </Card>

      {/* Closest to Pin */}
      <Card className="bg-[#121212] border-white/10">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-white text-base">Closest to Pin</CardTitle>
            {editing ? (
              <Switch
                checked={settings.ctp?.enabled}
                onCheckedChange={(v) => setSettings({
                  ...settings,
                  ctp: { ...settings.ctp, enabled: v }
                })}
              />
            ) : (
              <Badge className={settings.ctp?.enabled ? 'bg-yellow-500/20 text-yellow-400' : 'bg-zinc-500/20 text-zinc-400'}>
                {settings.ctp?.enabled ? 'Enabled' : 'Disabled'}
              </Badge>
            )}
          </div>
        </CardHeader>
        {settings.ctp?.enabled && (
          <CardContent className="pt-2">
            <div className="mb-2">
              <span className="text-sm text-zinc-400">CTP Holes:</span>
              <div className="flex flex-wrap gap-2 mt-1">
                {settings.ctp?.holes?.map(hole => (
                  <Badge key={hole} className="bg-yellow-500/20 text-yellow-400">
                    Hole {hole}
                  </Badge>
                ))}
              </div>
            </div>
            <div className="flex gap-4 text-sm">
              <span className="text-zinc-400">Prize pot:</span>
              <span className="text-white font-semibold">${settings.ctp?.total_pot || 0}</span>
              {settings.ctp?.holes?.length > 0 && (
                <span className="text-zinc-500">
                  (${((settings.ctp?.total_pot || 0) / settings.ctp.holes.length).toFixed(2)}/hole)
                </span>
              )}
            </div>
          </CardContent>
        )}
      </Card>

      {/* Custom Rules */}
      <Card className="bg-[#121212] border-white/10">
        <CardHeader className="pb-2">
          <CardTitle className="text-white text-base">Custom Rules</CardTitle>
        </CardHeader>
        <CardContent>
          {editing ? (
            <Textarea
              value={settings.custom_rules || ''}
              onChange={(e) => setSettings({ ...settings, custom_rules: e.target.value })}
              placeholder="Add any special rules or instructions..."
              className="bg-[#1A1A1A] border-white/20"
            />
          ) : (
            <p className="text-zinc-300 text-sm whitespace-pre-wrap">
              {settings.custom_rules || 'No custom rules set'}
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
